import { useCallback, useMemo } from 'react';
import { useUploadValidation } from './useUploadValidation';
import { useAISettings } from './useAISettings';
import { useFileProcessing } from './useFileProcessing';
import { useErrorManagement } from './useErrorManagement';
import { UploadedFile, InvalidFile, FileWithWarnings } from './useUploadValidation';
import { AISettingsData } from '../../utils/aiSettingsStorage';
import { CreditInfo } from '../../stores/creditStore';
import { calculateRequiredCredits } from '../../utils/creditCalculation';

export interface UseUploadReturn {
  // File states (from useUploadValidation)
  files: UploadedFile[];
  invalidFiles: InvalidFile[];
  filesWithWarnings: FileWithWarnings[];
  duplicateFiles: File[];
  
  // UI states (from useFileProcessing)
  isDragOver: boolean;
  isValidating: boolean;
  isUploading: boolean;
  isCreditValidating: boolean;
  showDetails: boolean;
  showCreditModal: boolean;
  uploadStatus: {
    step: 'creating-album' | 'reserving-credits' | 'validating' | 'redirecting' | 'error' | 'idle';
    message?: string;
    errorFileName?: string;
  };
  albumCreationResult?: {
    success: boolean;
    albumSlug?: string;
    albumId?: string;
    message?: string;
  };
  
  // AI Settings (from useAISettings)
  aiSettings: AISettingsData;
  
  // Error states (from useErrorManagement)
  uploadErrors: string[];
  validationErrors: string[];
  systemErrors: string[];
  hasErrors: boolean;
  
  // Credit info
  creditInfo: CreditInfo;
  creditValidationResult?: {
    hasEnoughCredits: boolean;
    currentBalance: number;
    requiredCredits: number;
    shortfall: number;
    filesValidated: number;
  };
  
  // Handlers - delegated to appropriate modules
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleToggleDetails: () => void;
  handleClearAll: () => void;
  handleClearErrors: () => void;
  handleRemoveFile: (fileId: string) => void;
  handleRemoveError: (errorId: string) => void;
  handleSettingsChange: (settings: AISettingsData) => void;
  handleResetSettings: () => void;
  handleUpload: () => Promise<void>;
  handleCreateAlbum: () => Promise<void>;
  handleCloseCreditModal: () => void;
  handleBuyCredits: () => void;
  
  // Utilities
  formatFileSize: (bytes: number) => string;
  updateFileStatus: (fileId: string, status: 'QUEUING' | 'VALIDATING' | 'UPLOADING' | 'PROCESSING' | 'COMPLETE' | 'FAILED') => void;
  updateFileValidationResult: (fileId: string, result: any) => void;
}

export const useUpload = (): UseUploadReturn => {
  // Initialize all specialized hooks
  const uploadValidation = useUploadValidation();
  const aiSettingsHook = useAISettings();
  const errorManagement = useErrorManagement();
  
  // Simple credit calculation - balance will be provided by parent component
  const creditInfo = useMemo((): CreditInfo => {
    const requiredCredits = calculateRequiredCredits(
      uploadValidation.files.map(file => ({
        name: file.file.name,
        type: file.file.type,
        size: file.file.size
      }))
    );
    
    return {
      balance: 0, // Will be overridden by parent component
      required: requiredCredits,
      cost_per_file: 1 // This is kept for backward compatibility but actual calculation uses proper logic
    };
  }, [uploadValidation.files]);
  
  const fileProcessing = useFileProcessing(
    (files) => {
      // Files are already File[] from drag/drop or input
      return uploadValidation.processFiles(files);
    },
    () => {}, // Removed debug logging
    (error) => errorManagement.addUploadError((error as Error).message),
    aiSettingsHook.aiSettings,
    uploadValidation.files,
    creditInfo,
    uploadValidation.updateFileStatus,
    uploadValidation.updateFileValidationResult
  );
  
  // Utility function for file size formatting
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);
  
  // Orchestrate upload process
  const handleUpload = useCallback(async () => {
    if (uploadValidation.files.length === 0) return;
    
    try {
      await fileProcessing.handleUpload();
    } catch (error) {
      errorManagement.addUploadError((error as Error).message);
    }
  }, [uploadValidation.files.length, fileProcessing, errorManagement]);
  
  const handleCreateAlbum = useCallback(async () => {
    try {
      await fileProcessing.handleCreateAlbum();
      uploadValidation.handleClearAll();
    } catch (error) {
      errorManagement.addUploadError((error as Error).message);
    }
  }, [fileProcessing, uploadValidation, errorManagement]);
  
  // Combine error removal handlers
  const handleRemoveError = useCallback((errorId: string) => {
    uploadValidation.handleRemoveError(errorId);
    // Error management handles its own errors internally
  }, [uploadValidation]);
  
  // Combine clear handlers
  const handleClearAll = useCallback(() => {
    uploadValidation.handleClearAll();
    errorManagement.clearErrors('all');
  }, [uploadValidation, errorManagement]);
  
  const handleClearErrors = useCallback(() => {
    uploadValidation.handleClearErrors();
    errorManagement.clearErrors('all');
  }, [uploadValidation, errorManagement]);
  
  return {
    // File states from uploadValidation
    files: uploadValidation.files,
    invalidFiles: uploadValidation.invalidFiles,
    filesWithWarnings: uploadValidation.filesWithWarnings,
    duplicateFiles: uploadValidation.duplicateFiles,
    
    // UI states from fileProcessing
    isDragOver: fileProcessing.isDragOver,
    isValidating: fileProcessing.isValidating,
    isUploading: fileProcessing.isUploading,
    isCreditValidating: fileProcessing.isCreditValidating,
    showDetails: fileProcessing.showDetails,
    showCreditModal: fileProcessing.showCreditModal,
    uploadStatus: fileProcessing.uploadStatus,
    
    // AI Settings from aiSettingsHook
    aiSettings: aiSettingsHook.aiSettings,
    
    // Error states from errorManagement
    uploadErrors: errorManagement.uploadErrors,
    validationErrors: errorManagement.validationErrors,
    systemErrors: errorManagement.systemErrors,
    hasErrors: errorManagement.hasErrors,
    
    // Credit info
    creditInfo,
    creditValidationResult: fileProcessing.creditValidationResult,
    albumCreationResult: fileProcessing.albumCreationResult,
    
    // Handlers - delegate to appropriate modules
    handleDragOver: fileProcessing.handleDragOver,
    handleDragLeave: fileProcessing.handleDragLeave,
    handleDrop: fileProcessing.handleDrop,
    handleFileInput: fileProcessing.handleFileInput,
    handleToggleDetails: fileProcessing.handleToggleDetails,
    handleClearAll,
    handleClearErrors,
    handleRemoveFile: uploadValidation.handleRemoveFile,
    handleRemoveError,
    handleSettingsChange: aiSettingsHook.handleSettingsChange,
    handleResetSettings: aiSettingsHook.handleResetSettings,
    handleUpload,
    handleCreateAlbum,
    handleCloseCreditModal: fileProcessing.handleCloseCreditModal,
    handleBuyCredits: fileProcessing.handleBuyCredits,
    
    // Utilities
    formatFileSize,
    updateFileStatus: uploadValidation.updateFileStatus,
    updateFileValidationResult: uploadValidation.updateFileValidationResult
  };
};